/**
 * Authentication Service
 *
 * Handles LINE token exchange, token refresh, and session management
 * Uses apiClient for all backend communication
 */

import { apiClient, HeaderType, isUserNotFoundError } from "@/lib/api-client";
import type { APIError } from "@/lib/api-client";
import type {
  TokenExchangeRequest,
  TokenExchangeResponse,
  TokenRefreshRequest,
  TokenRefreshResponse,
} from "@/types/auth";

/**
 * AuthService
 *
 * Service class for authentication operations
 * All methods use apiClient for consistent error handling and headers
 */
export class AuthService {
  /**
   * Exchange LINE tokens for server tokens
   *
   * @param lineIdToken - LINE ID token from liff.getIDToken()
   * @param lineAccessToken - LINE access token from liff.getAccessToken()
   * @param preferredRoleType - Preferred role type (defaults to 'customer')
   * @returns Token exchange response with access tokens and user info
   * @throws Will redirect to /signup with context if user not found (new user)
   * @throws Will display error message for other 401 errors
   */
  static async exchangeLineToken(
    lineIdToken: string,
    lineAccessToken: string,
    preferredRoleType: "customer" | "store" | "station" = "customer",
  ): Promise<TokenExchangeResponse> {
    const request: TokenExchangeRequest = {
      id_token: lineIdToken,
      access_token: lineAccessToken,
      preferred_role_type: preferredRoleType,
    };

    try {
      // Token exchange endpoint receives LINE tokens in request body
      // No auth header needed - tokens are in the request body
      return await apiClient.post<TokenExchangeResponse>("/auth/line", request, {
        credentials: "include",
        headerTypes: [HeaderType.JSON_CONTENT, HeaderType.NO_CACHE],
      });
    } catch (error) {
      console.log("error", error);
      console.log("error instanceof Error", error instanceof Error);
      console.log("(error as APIError).statusCode", (error as APIError).statusCode);
      if (error instanceof Error && (error as APIError).statusCode === 401) {
        const apiError = error as APIError & { errorBody?: { error_description?: string } };

        // Check if this is a "User not found" error (new user registration)
        if (apiError.errorBody && isUserNotFoundError(apiError.errorBody)) {
          const redirectUrl = typeof window !== "undefined"
            ? `${window.location.pathname}${window.location.search}`
            : "/";

          if (typeof window !== "undefined" && window.location.pathname !== "/signup") {
            window.location.href = `/signup?redirect=${encodeURIComponent(redirectUrl)}`;
            throw new Error("User not found - redirecting to signup");
          }
        } else {
          const errorMessage = apiError.message || "Authentication failed";
          console.error("LINE token exchange failed:", errorMessage);
          throw new Error(`Authentication failed: ${errorMessage}`);
        }
      }

      // Re-throw other errors
      throw error;
    }
  }

  /**
   * Refresh access token using refresh token
   *
   * @param csrfToken - CSRF token from localStorage
   * @param roleId - Selected role ID
   * @param clientId - Client identifier (always 'liff-app')
   * @returns Token refresh response with new tokens
   */
  static async refreshToken(
    csrfToken: string,
    roleId: string,
    clientId: string = "liff-app",
  ): Promise<TokenRefreshResponse> {
    const request: TokenRefreshRequest = {
      grant_type: "refresh_token",
      client_id: clientId,
      role_id: roleId,
    };

    return apiClient.post<TokenRefreshResponse>("/auth/token", request, {
      // Include credentials for cookie support
      credentials: "include",
      // Prevent infinite redirect loop on 401
      skipAuthRedirect: true,
      // Use minimal headers - no Authorization needed (refresh token in cookie)
      headerTypes: [HeaderType.JSON_CONTENT, HeaderType.NO_CACHE],
      // Add CSRF token header
      headers: {
        "X-CSRF-Token": csrfToken,
      },
    });
  }

  /**
   * Revoke refresh token and logout
   *
   * @returns Promise that resolves when revoke is complete
   */
  static async revokeToken(): Promise<void> {
    try {
      // Attempt to revoke token on backend
      await apiClient.post(
        "/auth/revoke",
        {},
        {
          credentials: "include",
          // Skip auth redirect on 401 (already logged out)
          skipAuthRedirect: true,
        },
      );
    } catch (error) {
      // Ignore errors - we'll clear local state regardless
      console.warn("Token revoke failed, clearing local state anyway:", error);
    }
  }
}
